import { useState, useEffect, useRef, useCallback } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import QRCodeLib from "qrcode";
import { msalConfig, graphScopes } from "./authConfig";
import { fetchUserProfile, fetchUserPhoto } from "./graphService";

const msalInstance = new PublicClientApplication(msalConfig);

// ─── Brand Themes ─────────────────────────────────────────────────────────────
const THEMES = [
  { id: "ddb",      label: "DDB",      dark: false, bg: "#ffffff", accent: "#ffcc00", text: "#111111", sub: "#666666", strip: "#ffffff", logo: "/logos/ddb.svg",     logoFilter: "none",       keywords: ["ddb"],      emailDomains: ["ddb.com","athens.ddb.com"] },
  { id: "lama",     label: "Lama",     dark: false, bg: "#ffffff", accent: "#111111", text: "#111111", sub: "#666666", strip: "#ffffff", logo: "/logos/lama.png",    logoFilter: "none",       keywords: ["lama"],     emailDomains: ["lamaddb.com"] },
  { id: "omikron",  label: "Omikron",  dark: false, bg: "#ffffff", accent: "#cc0000", text: "#111111", sub: "#666666", strip: "#f2f2f2", logo: "/logos/omikron.png",  logoFilter: "none",       keywords: ["omikron"],  emailDomains: ["omikron.gr","omikrongroup.gr"] },
  { id: "oktasoft", label: "Oktasoft", dark: false, bg: "#ffffff", accent: "#6b2d9e", text: "#111111", sub: "#666666", strip: "#f3eefa", logo: "/logos/oktasoft.png", logoFilter: "none",       keywords: ["oktasoft"], emailDomains: ["oktasoft.gr"] },
  { id: "briefme",  label: "BriefMe",  dark: false, bg: "#ffffff", accent: "#1a73e8", text: "#111111", sub: "#666666", strip: "#e8f0fd", logo: "/logos/briefme.svg",  logoFilter: "none",       keywords: ["briefme"],  emailDomains: ["briefme.gr"] },
  { id: "ignota",   label: "Ignota",   dark: false, bg: "#ffffff", accent: "#111111", text: "#111111", sub: "#666666", strip: "#f5f5f5", logo: "/logos/ignota.svg",   logoFilter: "none",       keywords: ["ignota"],   emailDomains: ["ignota.io"] },
];

const DEFAULT_THEME = THEMES[0];

function themeFromProfile(company, email) {
  const lowerCompany = (company || "").toLowerCase();
  const lowerEmail   = (email   || "").toLowerCase();
  const domain = lowerEmail.split("@")[1] || "";
  const byDomain = THEMES.find(t => t.emailDomains.some(d => domain === d || domain.endsWith("." + d)));
  if (byDomain) return byDomain;
  const byCompany = THEMES.find(t => t.keywords.some(k => lowerCompany.includes(k)));
  if (byCompany) return byCompany;
  return DEFAULT_THEME;
}

// ─── Real scannable QR Code ───────────────────────────────────────────────────
function QRCode({ value, size = 120, fg = "#000000", bg = "#ffffff" }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !value) return;
    QRCodeLib.toCanvas(ref.current, value, {
      width: size,
      margin: 1,
      color: { dark: fg, light: bg },
    });
  }, [value, size, fg, bg]);
  return <canvas ref={ref} width={size} height={size} style={{ borderRadius: 4 }} />;
}

// ─── Wallet Card ──────────────────────────────────────────────────────────────
function WalletCard({ data, theme: t, photo, flipped, onClick, showMobile }) {
  const vcf = buildVCard(data, showMobile);

  return (
    <div style={{ perspective: 900 }} onClick={onClick}>
      <div style={{
        width: 320, height: 202, borderRadius: 18,
        position: "relative", cursor: "pointer",
        fontFamily: "'Graphik','Helvetica Neue',sans-serif",
        boxShadow: "0 24px 64px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.08)",
        transformStyle: "preserve-3d",
        transition: "transform 0.65s cubic-bezier(0.4,0,0.2,1)",
        transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
      }}>

        {/* ── FRONT ── */}
        <div style={{
          position:"absolute", inset:0, borderRadius:18, overflow:"hidden",
          background: t.bg,
          backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden",
          padding:"20px 22px",
          display:"flex", flexDirection:"column", justifyContent:"space-between",
        }}>
          <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:t.accent }} />

          <div style={{ display:"flex", justifyContent:"flex-start", alignItems:"flex-start" }}>
            <img src={t.logo} alt={t.label} style={{
              height:28, maxWidth:110, objectFit:"contain",
              filter: t.logoFilter,
            }} onError={e => e.target.style.display="none"} />
          </div>

          <div>
            <div style={{ color:t.text, fontSize:19, fontWeight:600, letterSpacing:-0.3, lineHeight:1.2 }}>
              {data.firstName||"First"} {data.lastName||"Last"}
            </div>
            {data.title && (
              <div style={{ color:t.accent, fontSize:10, letterSpacing:1.5, textTransform:"uppercase", marginTop:3 }}>
                {data.title}
              </div>
            )}
            {data.department && data.department.toLowerCase() !== "management" && (
              <div style={{ color:t.sub, fontSize:10, marginTop:2 }}>{data.department}</div>
            )}
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
            {data.email && <span style={{ color:t.sub, fontSize:10 }}>✉ {data.email}</span>}
            {data.businessPhone && <span style={{ color:t.sub, fontSize:10 }}>T: {data.businessPhone}</span>}
            {showMobile && data.mobile && <span style={{ color:t.sub, fontSize:10 }}>M: {data.mobile}</span>}
            {data.website && <span style={{ color:t.sub, fontSize:10 }}>{data.website.replace(/^https?:\/\//, "")}</span>}
          </div>

          <div style={{ position:"absolute", bottom:8, right:12, color:t.sub, fontSize:9, opacity:0.4 }}>tap to flip ↺</div>
        </div>

        {/* ── BACK ── */}
        <div style={{
          position:"absolute", inset:0, borderRadius:18, overflow:"hidden",
          background: "#ffffff",
          backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden",
          transform:"rotateY(180deg)",
          display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:8,
        }}>
          <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:t.accent }} />
          <QRCode value={vcf} size={120} fg="#000000" bg="#ffffff" />
          <div style={{ color:"#888", fontSize:9, letterSpacing:1.5, textTransform:"uppercase" }}>
            Scan to save contact
          </div>
          <img src={t.logo} alt={t.label} style={{
            height:16, objectFit:"contain",
            filter: t.dark ? "none" : t.logoFilter,
            opacity:0.5, marginTop:2,
          }} onError={e => e.target.style.display="none"} />
        </div>
      </div>
    </div>
  );
}

// ─── vCard builder (shared between card QR and download) ─────────────────────
function buildVCard(data, showMobile) {
  return [
    "BEGIN:VCARD","VERSION:3.0",
    `FN:${data.firstName} ${data.lastName}`,
    `N:${data.lastName};${data.firstName};;;`,
    data.title         ? `TITLE:${data.title}`                      : "",
    data.company       ? `ORG:${data.company}`                      : "",
    data.businessPhone ? `TEL;TYPE=WORK,VOICE:${data.businessPhone}` : "",
    showMobile && data.mobile ? `TEL;TYPE=CELL:${data.mobile}`      : "",
    data.email         ? `EMAIL:${data.email}`                      : "",
    data.website       ? `URL:${data.website}`                      : "",
    data.address       ? `ADR:;;${data.address};;;;`                : "",
    "END:VCARD",
  ].filter(Boolean).join("\n");
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen() {
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setLoading(true);
    try { await instance.loginPopup(graphScopes); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight:"100vh", background:"#f5f5f5",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      fontFamily:"'Graphik','Helvetica Neue',sans-serif",
    }}>
      <div style={{ textAlign:"center", padding:40, maxWidth:400 }}>
        <div style={{
          width:64, height:64, borderRadius:16,
          background:"#111", border:"1px solid #222",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:28, margin:"0 auto 28px",
          boxShadow:"0 8px 32px rgba(0,0,0,0.15)",
        }}>🪪</div>

        <h1 style={{ color:"#111", fontSize:28, fontWeight:700, letterSpacing:-1, margin:"0 0 8px", fontFamily:"'Graphik','Helvetica Neue',sans-serif" }}>
          Business Cards
        </h1>
        <p style={{ color:"#888", fontSize:13, margin:"0 0 40px", lineHeight:1.6 }}>
          Sign in with your Microsoft account to generate your business card from Entra ID.
        </p>

        <button onClick={handleLogin} disabled={loading} style={{
          background: loading ? "#eee" : "#fff",
          color:"#111", border:"1px solid #ddd", borderRadius:12,
          padding:"14px 28px", fontSize:13, fontWeight:600,
          cursor: loading ? "not-allowed" : "pointer",
          display:"flex", alignItems:"center", gap:12,
          margin:"0 auto",
          boxShadow:"0 2px 12px rgba(0,0,0,0.08)",
          transition:"all 0.2s", opacity: loading ? 0.6 : 1,
          fontFamily:"'Graphik','Helvetica Neue',sans-serif",
        }}>
          <MicrosoftLogo />
          {loading ? "Signing in…" : "Sign in with Microsoft"}
        </button>
      </div>
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  );
}

// ─── Toggle switch ────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, accent }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width:36, height:20, borderRadius:10, cursor:"pointer",
        background: checked ? accent : "#ddd",
        position:"relative", transition:"background 0.2s", flexShrink:0,
      }}
    >
      <div style={{
        position:"absolute", top:2, left: checked ? 18 : 2,
        width:16, height:16, borderRadius:"50%",
        background:"#fff",
        boxShadow:"0 1px 4px rgba(0,0,0,0.2)",
        transition:"left 0.2s",
      }}/>
    </div>
  );
}

// ─── Card Generator ───────────────────────────────────────────────────────────
function CardGenerator() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const [data, setData]         = useState({ firstName:"", lastName:"", title:"", company:"", department:"", businessPhone:"", mobile:"", email:"", website:"", address:"" });
  const [photo, setPhoto]       = useState(null);
  const [theme, setTheme]       = useState(DEFAULT_THEME);
  const [flipped, setFlipped]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [synced, setSynced]     = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  // showMobile: only relevant when extensionAttribute1 is populated
  const [showMobile, setShowMobile] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [profile, userPhoto] = await Promise.all([
          fetchUserProfile(instance, account),
          fetchUserPhoto(instance, account),
        ]);
        setData(profile);
        setTheme(themeFromProfile(profile.company, profile.email));
        if (userPhoto) setPhoto(userPhoto);
        setSynced(true);
      } catch (err) {
        setError("Could not load profile: " + err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [instance, account]);

  const downloadVCard = useCallback(() => {
    const lines = buildVCard(data, showMobile);
    const blob = new Blob([lines], { type:"text/vcard" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `${data.firstName}_${data.lastName}.vcf`;
    a.click(); URL.revokeObjectURL(url);
    setDownloaded(true);
    setTimeout(() => setDownloaded(false), 2500);
  }, [data, showMobile]);

  const handleLogout = () => instance.logoutPopup();

  const inputStyle = {
    background:"#f5f5f5", border:"1px solid #e8e8e8", borderRadius:8,
    color:"#444444", padding:"9px 12px", fontSize:13,
    width:"100%", boxSizing:"border-box", outline:"none",
    fontFamily:"'Graphik','Helvetica Neue',sans-serif",
    cursor:"default", userSelect:"text",
  };

  const labelStyle = {
    color:"#999", fontSize:10, letterSpacing:1.5,
    textTransform:"uppercase", marginBottom:5, display:"block",
  };

  // Static fields shown in the form (read-only, from Entra)
  const fields = [
    ["firstName","First Name"],["lastName","Last Name"],
    ["title","Job Title"],["company","Company"],
    ["department","Department"],["businessPhone","Office Phone"],
    ["email","Email"],["website","Website"],
    ["address","Location"],
  ];

  if (loading) return (
    <div style={{
      minHeight:"100vh", background:"#f5f5f5",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      gap:16, fontFamily:"'Graphik','Helvetica Neue',sans-serif", color:"#999",
    }}>
      <div style={{ fontSize:28 }}>🪪</div>
      <div style={{ fontSize:12, letterSpacing:2, textTransform:"uppercase" }}>Loading from Entra…</div>
      <div style={{ width:160, height:2, background:"#e0e0e0", borderRadius:2, overflow:"hidden" }}>
        <div style={{ height:"100%", background:"#111", borderRadius:2, animation:"slide 1.4s ease-in-out infinite", width:"40%" }}/>
      </div>
      <style>{`@keyframes slide{0%{transform:translateX(-100%)}100%{transform:translateX(500%)}}`}</style>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:"#f5f5f5", fontFamily:"'Graphik','Helvetica Neue',sans-serif", color:"#111" }}>

      {/* Header */}
      <div style={{
        borderBottom:"1px solid #e8e8e8", padding:"16px 32px",
        display:"flex", alignItems:"center", justifyContent:"space-between",
        background:"#ffffff",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:16 }}>🪪</span>
          <span style={{ fontSize:17, fontWeight:700, letterSpacing:-0.5, color:"#111111", fontFamily:"'Graphik','Helvetica Neue',sans-serif" }}>
            Business Cards
          </span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          {photo && <img src={photo} alt="" style={{ width:30, height:30, borderRadius:"50%", objectFit:"cover" }}/>}
          <span style={{ color:"#888", fontSize:12 }}>{account?.name || account?.username}</span>
          <button onClick={handleLogout} style={{
            background:"transparent", border:"1px solid #e0e0e0", borderRadius:8,
            color:"#888", fontSize:11, padding:"6px 12px", cursor:"pointer",
            fontFamily:"'Graphik','Helvetica Neue',sans-serif",
          }}>Sign out</button>
        </div>
      </div>

      {error && (
        <div style={{ background:"#fff0f0", border:"1px solid #ffd0d0", color:"#cc0000", padding:"12px 32px", fontSize:12 }}>
          ⚠ {error}
        </div>
      )}

      <div style={{ maxWidth:940, margin:"0 auto", padding:"36px 24px", display:"flex", gap:52, flexWrap:"wrap" }}>

        {/* FIRST: Preview */}
        <div style={{ flex:"1 1 340px", display:"flex", flexDirection:"column", alignItems:"center", gap:28 }}>
          <div style={{ fontSize:10, letterSpacing:2, color:"#999", textTransform:"uppercase", alignSelf:"flex-start" }}>
            Preview
          </div>

          {/* Phone frame */}
          <div style={{
            background:"#e8e8e8", borderRadius:32,
            padding:"28px 22px 24px",
            border:"1px solid #d0d0d0",
            display:"flex", flexDirection:"column", alignItems:"center", gap:18,
            boxShadow:"0 8px 40px rgba(0,0,0,0.1)",
            width:"100%", maxWidth:378,
          }}>
            <div style={{ width:72, height:4, background:"#bbb", borderRadius:2 }}/>
            <div style={{ fontSize:10, letterSpacing:2, color:"#999", textTransform:"uppercase" }}>Wallet Pass</div>
            <WalletCard data={data} theme={theme} photo={photo} flipped={flipped} onClick={() => setFlipped(f => !f)} showMobile={showMobile} />
            <div style={{ fontSize:10, color:"#aaa" }}>Tap card · QR code on back</div>
          </div>

          {/* Actions */}
          <div style={{ display:"flex", flexDirection:"column", gap:10, width:"100%", maxWidth:378 }}>
            <button onClick={downloadVCard} style={{
              background: theme.accent,
              color: "#ffffff",
              border:"none", borderRadius:12, padding:"14px 24px",
              fontSize:13, fontWeight:700, cursor:"pointer",
              letterSpacing:0.3, transition:"opacity 0.2s",
              opacity: downloaded ? 0.7 : 1,
              fontFamily:"'Graphik','Helvetica Neue',sans-serif",
            }}>
              {downloaded ? "✓ Downloaded!" : "↓ Download vCard (.vcf)"}
            </button>
          </div>
        </div>

        {/* SECOND: Form */}
        <div style={{ flex:"1 1 300px", minWidth:260 }}>
          <div style={{ fontSize:10, letterSpacing:2, color:"#999", textTransform:"uppercase", marginBottom:22 }}>
            Your Details
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            {fields.map(([key, label]) => (
              <div key={key} style={{ gridColumn:["email","website","address"].includes(key) ? "1/-1" : "auto" }}>
                <label style={labelStyle}>{label}</label>
                <input
                  style={inputStyle}
                  value={data[key]}
                  readOnly
                  placeholder="—"
                />
              </div>
            ))}

            {/* Mobile row — always shown, but toggle only if extensionAttribute1 is set */}
            <div style={{ gridColumn:"1/-1" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:5 }}>
                <label style={{ ...labelStyle, marginBottom:0 }}>Mobile</label>
                {data.mobile && (
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:10, color: showMobile ? "#444" : "#aaa" }}>
                      {showMobile ? "Shown on card" : "Hidden from card"}
                    </span>
                    <Toggle checked={showMobile} onChange={setShowMobile} accent={theme.accent} />
                  </div>
                )}
              </div>
              <input
                style={{
                  ...inputStyle,
                  color: data.mobile ? "#444444" : "#cccccc",
                }}
                value={data.mobile || ""}
                readOnly
                placeholder="Not set — contact IT to add your mobile"
              />
              {data.mobile && (
                <div style={{ marginTop:5, fontSize:10, color:"#aaa", lineHeight:1.5 }}>
                  Your mobile is not visible in Outlook or Teams. Only you control whether it appears on your card.
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop:14, fontSize:11, color:"#aaa", lineHeight:1.5 }}>
            Fields are synced from Entra ID. To update your details, contact IT.
          </div>

          {/* Brand picker */}
          <div style={{ marginTop:28 }}>
            <div style={labelStyle}>Brand</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {THEMES.map(th => (
                <button key={th.id} onClick={() => setTheme(th)} style={{
                  padding:"6px 14px", borderRadius:8, border:"1px solid",
                  borderColor: theme.id===th.id ? th.accent : "#e0e0e0",
                  background: theme.id===th.id ? th.accent + "18" : "#ffffff",
                  color: theme.id===th.id ? th.accent : "#888",
                  fontSize:11, cursor:"pointer", transition:"all 0.2s",
                  fontFamily:"'Graphik','Helvetica Neue',sans-serif", fontWeight: theme.id===th.id ? 600 : 400,
                }}>{th.label}</button>
              ))}
            </div>
          </div>
        </div>

      </div>

      <div style={{ textAlign:"center", color:"#ccc", fontSize:10, paddingBottom:32, letterSpacing:2 }}>
        BUSINESS CARDS · POWERED BY MICROSOFT ENTRA
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
function AppInner() {
  const isAuthenticated = useIsAuthenticated();
  return isAuthenticated ? <CardGenerator /> : <LoginScreen />;
}

export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AppInner />
    </MsalProvider>
  );
}
